import { commandBody, attachmentIdentity } from './attachments';
export { validateHelpdeskAttachments } from './attachments';
import { helpdeskEmailDeliverySchema } from './email-delivery';
import type { operations } from '@molchanovs/api-contract';
import { useRef } from 'react';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@shared/api';
import { clearQueryResponseCache, createQuery } from '@shared/lib/query-helpers';
import type { HelpdeskTask } from './queries';

const optionSchema = z.object({ id: z.uuid(), dbValue: z.string(), display: z.string() });
const personSchema = z.object({
  id: z.uuid(),
  displayName: z.string().nullable(),
  avatarUrl: z.url().nullable().optional(),
  customerId: z.uuid().optional(),
});
export const helpdeskTaskSchema = z.object({
  id: z.uuid(),
  taskId: z.string().nullable(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  status: optionSchema.nullable(),
  feedbackCategory: optionSchema.nullable(),
  startAt: z.iso.datetime().nullable(),
  dueAt: z.iso.datetime().nullable(),
  isClosed: z.boolean(),
  origin: optionSchema.nullable(),
  createdAt: z.iso.datetime().nullable(),
  author: personSchema.nullable(),
  assignees: z.array(personSchema),
  contactEmail: z.string().nullable(),
  explicitlyAllowedUsers: z.array(personSchema),
  emailDelivery: helpdeskEmailDeliverySchema.nullable().optional(),
  deletionState: z
    .object({ status: z.enum(['pending', 'failed']) })
    .nullable()
    .optional(),
}) satisfies z.ZodType<HelpdeskTask>;

export const createHelpdeskTaskSchema: z.ZodType<CreateHelpdeskTask> = z
  .object({
    title: z.string().trim().min(1).max(300),
    message: z.string().trim().min(1).max(20000),
    requester: z.union([
      z.object({ customerId: z.uuid() }).strict(),
      z.object({ contactEmail: z.email().max(320) }).strict(),
    ]),
    originId: z.uuid(),
    feedbackCategoryId: z.uuid().optional(),
    privacyAcknowledged: z.boolean().optional(),
  })
  .strict();
export const createHelpdeskReplySchema: z.ZodType<CreateHelpdeskReply> = z
  .object({
    message: z.string().trim().min(1).max(20000),
    kind: z.enum(['support-reply', 'internal-comment']),
    delivery: z.object({ channelId: z.uuid() }).strict().optional(),
  })
  .strict();
export const helpdeskCommandResponseSchema: z.ZodType<HelpdeskCommandResponse> = z.object({
  data: helpdeskTaskSchema,
  meta: z.object({
    emailDelivery: z.object({ id: z.uuid() }).optional(),
    bubbleSync: z.object({
      operationId: z.uuid(),
      status: z.enum([
        'accepted',
        'delivery_pending',
        'processing',
        'retry_scheduled',
        'applied',
        'conflict',
        'manual_reconciliation',
        'terminal_failure',
        'superseded',
      ]),
    }),
  }),
});
export const helpdeskFormOptionsSchema: z.ZodType<HelpdeskFormOptions> = z.object({
  data: z.object({ origins: z.array(optionSchema), feedbackCategories: z.array(optionSchema) }),
});
export type CreateHelpdeskTask = Extract<
  NonNullable<operations['postApiV1Tasks']['requestBody']>['content']['application/json'],
  { requester: unknown }
>;
export type CreateHelpdeskReply = Extract<
  NonNullable<
    operations['postApiV1TasksByTaskUuidReplies']['requestBody']
  >['content']['application/json'],
  { kind: unknown }
>;
export type HelpdeskCommandResponse = Extract<
  operations['postApiV1Tasks']['responses'][201]['content']['application/json'],
  { meta: unknown }
>;
export type HelpdeskFormOptions = Extract<
  operations['getApiV1TasksFormOptions']['responses'][200]['content']['application/json'],
  { data: { origins: unknown } }
>;

export function helpdeskFormOptionsQuery(viewerAccessScope: string) {
  return createQuery({
    path: '/api/v1/tasks/form-options',
    queryKey: ['helpdesk', viewerAccessScope, 'form-options'],
    schema: helpdeskFormOptionsSchema,
    retry: false,
  });
}

export interface HelpdeskAttachments {
  files?: File[];
}

export function createHelpdeskTask(
  input: CreateHelpdeskTask,
  idempotencyKey: string = crypto.randomUUID(),
  files: readonly File[] = []
) {
  return api.post<HelpdeskCommandResponse, CreateHelpdeskTask | FormData>(
    '/api/v1/tasks',
    commandBody(createHelpdeskTaskSchema.parse(input), files),
    { schema: helpdeskCommandResponseSchema, headers: { 'Idempotency-Key': idempotencyKey } }
  );
}
export function createHelpdeskReply(
  taskUuid: string,
  input: CreateHelpdeskReply,
  idempotencyKey: string = crypto.randomUUID(),
  files: readonly File[] = []
) {
  const id = z.uuid().parse(taskUuid);
  return api.post<HelpdeskCommandResponse, CreateHelpdeskReply | FormData>(
    `/api/v1/tasks/${encodeURIComponent(id)}/replies`,
    commandBody(createHelpdeskReplySchema.parse(input), files),
    { schema: helpdeskCommandResponseSchema, headers: { 'Idempotency-Key': idempotencyKey } }
  );
}

export function useCreateHelpdeskTask(viewerAccessScope: string) {
  const client = useQueryClient();
  const admission = useRef<{ payload: string; key: string } | null>(null);
  return useMutation({
    mutationFn: (input: CreateHelpdeskTask & HelpdeskAttachments) => {
      const { files = [], ...command } = input;
      const payload = JSON.stringify([
        viewerAccessScope,
        createHelpdeskTaskSchema.parse(command),
        files.map(attachmentIdentity),
      ]);
      if (admission.current?.payload !== payload)
        admission.current = { payload, key: crypto.randomUUID() };
      return createHelpdeskTask(command, admission.current.key, files);
    },
    retry: false,
    onSuccess: (response) => {
      admission.current = null;
      clearQueryResponseCache();
      client.setQueryData(['helpdesk', viewerAccessScope, 'detail', response.data.id], {
        data: response.data,
      });
      void client.invalidateQueries({ queryKey: ['helpdesk', viewerAccessScope, 'list'] });
    },
  });
}
export function useCreateHelpdeskReply(viewerAccessScope: string, taskUuid: string) {
  const client = useQueryClient();
  const admission = useRef<{ payload: string; key: string } | null>(null);
  return useMutation({
    mutationFn: (input: CreateHelpdeskReply & HelpdeskAttachments) => {
      const { files = [], ...command } = input;
      const payload = JSON.stringify([
        viewerAccessScope,
        taskUuid,
        createHelpdeskReplySchema.parse(command),
        files.map(attachmentIdentity),
      ]);
      if (admission.current?.payload !== payload)
        admission.current = { payload, key: crypto.randomUUID() };
      return createHelpdeskReply(taskUuid, command, admission.current.key, files);
    },
    retry: false,
    onSuccess: () => {
      admission.current = null;
      clearQueryResponseCache();
      void client.invalidateQueries({
        queryKey: ['helpdesk', viewerAccessScope],
        refetchType: 'none',
      });
    },
  });
}
